<template>
  <div class="rutherford-viz-3d">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>核式结构模型</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-top">
            <div class="counter-box">
               <label>大角度散射计数</label>
               <span :class="{ 'alert': scatterCount > 0 }">{{ scatterCount }}</span>
            </div>
         </div>
      </div>

      <!-- 参数控制 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <el-button type="primary" size="mini" @click="firing = !firing">
                  {{ firing ? '停止发射' : '开始发射 α 粒子' }}
               </el-button>
               <el-button size="mini" @click="reset">清空轨迹</el-button>
            </div>
         </div>

         <div class="theory-card">
            <i class="el-icon-view"></i>
            <p><strong>实验结论：</strong>绝大多数粒子穿过后，极少数粒子（约 1/8000）发生大角度偏转甚至反弹，证明了原子中心存在一个极小的<strong>原子核</strong>。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'RutherfordModelViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      firing: true,
      scatterCount: 0,
      particles: [],
      running: true,
      nextId: 0
    }
  },
  mounted() { this.initThree(); },
  beforeDestroy() { 
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(20, 30, 100);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Gold Nucleus (Center)
      const nucleusGeo = new THREE.SphereGeometry(2, 32, 32);
      const nucleusMat = new THREE.MeshPhongMaterial({ color: 0xffd700, emissive: 0xffd700, emissiveIntensity: 0.5 });
      this.nucleus = new THREE.Mesh(nucleusGeo, nucleusMat);
      this.scene.add(this.nucleus);

      // Visual helper (Core range)
      const coreRange = new THREE.Mesh(new THREE.SphereGeometry(15, 32, 32), new THREE.MeshBasicMaterial({ color: 0xffd700, wireframe: true, transparent: true, opacity: 0.05 }));
      this.scene.add(coreRange);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));
      const light = new THREE.PointLight(0xffffff, 1, 100);
      light.position.set(20, 20, 20);
      this.scene.add(light);

      this.animate();
    },
    reset() {
      this.particles.forEach(p => this.scene.remove(p.mesh));
      this.particles = [];
      this.scatterCount = 0;
    },
    spawnAlpha() {
       if (!this.firing) return;
       const y = (Math.random() - 0.5) * 40;
       const z = (Math.random() - 0.5) * 40;
       
       const mesh = new THREE.Mesh(new THREE.SphereGeometry(0.5, 8, 8), new THREE.MeshBasicMaterial({ color: 0xff4757 }));
       mesh.position.set(-100, y, z);
       this.scene.add(mesh);
       
       this.particles.push({
           id: this.nextId++,
           mesh: mesh,
           vx: 2,
           vy: 0,
           vz: 0,
           scattered: false
       });
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);

      if (Math.random() > 0.8) this.spawnAlpha();

      this.particles.forEach((p, idx) => {
          // Coulomb interaction simplified: F proportional to 1/r^2
          const dx = p.mesh.position.x - 0;
          const dy = p.mesh.position.y - 0;
          const dz = p.mesh.position.z - 0;
          const distSq = dx*dx + dy*dy + dz*dz;
          const dist = Math.sqrt(distSq);

          if (dist < 30) {
             const force = 50 / (distSq + 1);
             p.vx += (dx / dist) * force;
             p.vy += (dy / dist) * force;
             p.vz += (dz / dist) * force;

             if (force > 5 && !p.scattered) {
                p.scattered = true;
                this.scatterCount++;
             }
          }

          p.mesh.position.x += p.vx;
          p.mesh.position.y += p.vy;
          p.mesh.position.z += p.vz;

          if (Math.abs(p.mesh.position.x) > 120 || Math.abs(p.mesh.position.y) > 120) {
             this.scene.remove(p.mesh);
             this.particles.splice(idx, 1);
          }
      });

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.rutherford-viz-3d { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header { display: flex; justify-content: space-between; align-items: center; .title { font-size: 1rem; color: #fff; } .formula-chip { color: #ffd700; background: rgba(255,215,0,0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-3d-wrapper {
  flex: 1.5; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-top { position: absolute; top: 15px; left: 15px; .counter-box { background: rgba(255,255,255,0.05); padding: 8px 15px; border-radius: 8px; border-left: 3px solid #ff4757; label { display: block; font-size: 0.6rem; color: #888; } span { font-size: 1.1rem; color: #fff; font-family: monospace; &.alert { color: #ff4757; text-shadow: 0 0 8px #ff4757; } } } }
}
.bottom-controls { flex: 0.5; display: flex; flex-direction: column; gap: 12px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; .row { display: flex; gap: 10px; justify-content: center; } }
.theory-card { background: rgba(255, 215, 0, 0.05); padding: 12px 18px; border-radius: 12px; display: flex; gap: 12px; i { color: #ffd700; font-size: 1.2rem; } p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
